import React from 'react';
import {useHistory} from 'react-router-dom';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import illustrationWinBack from 'assets/customerInsights/ci-win-back.svg';
import illustrationRewardFrequent from 'assets/customerInsights/ci-reward-frequent.svg';
import illustrationConvertNew from 'assets/customerInsights/ci-convert-new.svg';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  OpportunitiesRow
} from '../styles';

const Card = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 10px;
  padding: 20px 16px;
  min-height: 199px;
  display: flex;
  box-shadow: 0px 8px 16px 0px rgba(221, 221, 221, 0.32);
  overflow: hidden;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  z-index: 1;
  padding-right: 111px;
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  line-height: 150%;
`;

const BADGE_STYLES = {
  recommended: {
    bg: 'linear-gradient(180deg, #4AE9A9 0%, #17C981 100%)',
    label: 'Recommended'
  },
  high_impact: {
    bg: 'linear-gradient(180deg, #FFD17C 0%, #FCA300 100%)',
    label: 'High impact'
  },
  quick_win: {
    bg: 'linear-gradient(180deg, #88AAFD 0%, #4671DB 100%)',
    label: 'Quick win'
  }
};

const Badge = styled.span`
  position: absolute;
  top: 20px;
  right: 16px;
  z-index: 2;
  padding: 1px 8px;
  border-radius: 25px;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 150%;
  color: #ffffff;
  background: ${({$type}) => BADGE_STYLES[$type]?.bg};
  box-shadow: 0px 5px 10px 0px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
`;

const CardBody = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #000000;
  margin: 0;
  line-height: 150%;
`;

const Cta = styled.button`
  align-self: flex-start;
  background: transparent;
  border: none;
  color: #ff2d38;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const Illustration = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 130px;
  height: 130px;
  object-fit: contain;
  display: block;
  pointer-events: none;
`;

const OPPORTUNITIES = [
  {
    id: 'win_back',
    badge: 'recommended',
    title: 'Win back lapsed customers',
    body:
      'Re-engagement deals recover 15–20% of lapsed customers. A small offer goes a long way.',
    ctaLabel: 'Create discount',
    ctaTarget: 'discounts',
    illustration: illustrationWinBack
  },
  {
    id: 'reward_frequent',
    badge: 'high_impact',
    title: 'Reward frequent customers',
    body:
      'Loyal customers order 3× more often. A VIP discount boosts their lifetime value.',
    ctaLabel: 'Create VIP offer',
    ctaTarget: 'discounts',
    illustration: illustrationRewardFrequent
  },
  {
    id: 'convert_new',
    badge: 'quick_win',
    title: 'Convert new to regulars',
    body:
      'Orders with a promo flyer see up to 30% higher reorder rates. Add a special offer to every bag.',
    ctaLabel: 'Add flyers to orders',
    ctaTarget: 'flyers',
    illustration: illustrationConvertNew
  }
];

const TopOpportunities = () => {
  const history = useHistory();
  const serviceFeatures = useSelector(
    (state) => state.user?.activeLocation?.AllowedServiceFeatures
  );

  const routeByTarget = {
    discounts: serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.DISCOUNTS_V2)
      ? '/discounts-v2'
      : '/discounts',
    flyers: '/flyers'
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Top opportunities</SectionTitle>
        <SectionSubtitle>
          Improve your sales with our recommendations for you
        </SectionSubtitle>
      </SectionHeader>
      <OpportunitiesRow>
        {OPPORTUNITIES.map((o) => (
          <Card key={o.id}>
            <Badge $type={o.badge}>{BADGE_STYLES[o.badge].label}</Badge>
            <CardContent>
              <CardTop>
                <CardTitle>{o.title}</CardTitle>
                <CardBody>{o.body}</CardBody>
              </CardTop>
              <Cta
                type='button'
                onClick={() => history.push(routeByTarget[o.ctaTarget])}
              >
                {o.ctaLabel}
              </Cta>
            </CardContent>
            {o.illustration && <Illustration src={o.illustration} alt='' />}
          </Card>
        ))}
      </OpportunitiesRow>
    </>
  );
};

export default TopOpportunities;
